import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type { SurveyQuestion, SurveyQuestionRequest } from './surveyTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check would drop it and the
    server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const surveyQuestionsApi = {
  /** Soft-deletes every question at once; resolves with how many. Answers already given stay. */
  clear: () => api<number>('/api/survey-questions/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<SurveyQuestion>>(`/api/survey-questions${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<SurveyQuestion>(`/api/survey-questions/${id}`, { signal }),

  /**
   * The ACTIVE questions, in the form's order.
   *
   * What the personnel profile's Survey panel is built from: it lists every question whether or
   * not the employee has answered it, so it needs the questionnaire rather than the answers.
   *
   * Deleted rows are left out — the panel asks what can be answered now. An answer already
   * recorded against a retired question keeps it, and the panel still states it.
   */
  options: (signal?: AbortSignal) =>
    api<SurveyQuestion[]>('/api/survey-questions/options', { signal }),

  /**
   * Adds a question.
   *
   * **Not idempotent on the number**, unlike the family-relation create: nothing reaches this
   * from inside an answer form, so a duplicate item number is a mistake worth naming rather
   * than a collision to absorb.
   */
  create: (request: SurveyQuestionRequest) =>
    api<SurveyQuestion>('/api/survey-questions', { method: 'POST', body: request }),

  update: (id: string, request: SurveyQuestionRequest) =>
    api<SurveyQuestion>(`/api/survey-questions/${id}`, { method: 'PUT', body: request }),

  /** Soft delete: the question leaves the form, the answers stay on the records. */
  deactivate: (id: string) => api<void>(`/api/survey-questions/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/survey-questions/${id}/restore`, { method: 'POST' }),
};
